import React, { useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store';
import { getCurrentUser } from '../store/authSlice';

interface ProtectedRouteProps {
  requiredRole?: 'ADMIN' | 'STAFF';
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ requiredRole }) => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, user, token, isLoading } = useAppSelector((state) => state.auth);

  useEffect(() => {
    // Only fetch user if: have token, not already authenticated, not loading
    if (token && !isAuthenticated && !isLoading) {
      dispatch(getCurrentUser());
    }
  }, [token, isAuthenticated, isLoading, dispatch]);

  // Show loading while checking authentication OR validating token
  if (isLoading || (token && !user)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-accent border-t-transparent"></div>
          <p className="mt-4 text-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated (and we're not loading)
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Check role-based access
  if (requiredRole && user.role !== requiredRole) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-surface rounded-lg shadow-lg border border-border p-8">
            <div className="text-6xl mb-4">🚫</div>
            <h2 className="text-2xl font-bold text-text-primary mb-2">Access Denied</h2>
            <p className="text-text-secondary mb-6">
              You don't have permission to access this page.
            </p>
            <p className="text-sm text-text-muted">
              Required role: <span className="font-medium text-accent">{requiredRole}</span>
              <br />
              Your role: <span className="font-medium">{user.role}</span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return <Outlet />;
};
